import type { CmmdBoardTask, CmmdTaskWorkflow } from "@cmmd-forge/contracts";
import { getStatusConfig } from "~/components/command-center/statusConfig";
import { taskStatusDescriptor } from "~/components/projects/boardColumns";
import { cn } from "~/lib/utils";

export const INTERRUPTED_TASK_STATUSES = ["blocked", "cancelled"] as const;

function configuredStageForLegacyStatus(
  status: string,
  workflow: CmmdTaskWorkflow,
): CmmdTaskWorkflow["stages"][number] | undefined {
  if (status === "cancelled") return undefined;
  const canonicalKey = status === "blocked" ? "todo" : status === "in_review" ? "human_qa" : status;
  return (
    workflow.stages.find((stage) => stage.key === canonicalKey) ??
    workflow.stages.find((stage) => stage.category === status)
  );
}

export function canDropTaskInWorkflowColumn(
  task: CmmdBoardTask,
  destinationId: string,
  workflow: CmmdTaskWorkflow | null | undefined,
): boolean {
  if (workflow == null) return true;
  if (INTERRUPTED_TASK_STATUSES.some((status) => status === destinationId)) return true;
  const isExactDestination = workflow.stages.some((stage) => stage.id === destinationId);
  if (!isExactDestination) {
    return task.workflowStageId == null && task.workflowResumeStageId == null;
  }
  return canMoveTaskToWorkflowStage(task, destinationId, workflow);
}

export interface TaskWorkflowGroup {
  readonly key: string;
  readonly label: string;
  readonly order: number;
  readonly category: string;
  readonly leading: React.ReactNode;
}

function statusLeading(status: string): React.ReactNode {
  const config = getStatusConfig(status);
  return (
    <span
      className={cn("inline-flex size-3 shrink-0 rounded-full border-2", config.circleClass)}
      aria-hidden="true"
    />
  );
}

export function workflowGroupForTask(
  task: CmmdBoardTask,
  workflow: CmmdTaskWorkflow | null | undefined,
): TaskWorkflowGroup {
  const exactStageId = task.workflowStageId ?? task.workflowResumeStageId;
  const stage =
    workflow?.stages.find((candidate) => candidate.id === exactStageId) ??
    (workflow != null ? configuredStageForLegacyStatus(task.status, workflow) : undefined);
  if (stage != null) {
    return {
      key: stage.id,
      label: stage.name,
      order: stage.position,
      category: stage.category,
      leading: (
        <span
          className="inline-flex size-3 shrink-0 rounded-full border border-border"
          style={{ backgroundColor: stage.color.startsWith("#") ? stage.color : undefined }}
          aria-hidden="true"
        />
      ),
    };
  }
  const descriptor = taskStatusDescriptor(task);
  return {
    ...descriptor,
    category: descriptor.key,
    order: (workflow?.stages.length ?? 0) + descriptor.order,
    leading: statusLeading(descriptor.key),
  };
}

export function workflowGroupsForTasks(
  tasks: readonly CmmdBoardTask[],
  workflow: CmmdTaskWorkflow | null | undefined,
): readonly TaskWorkflowGroup[] {
  const groups = new Map<string, TaskWorkflowGroup>();
  for (const task of tasks) {
    const group = workflowGroupForTask(task, workflow);
    groups.set(group.key, group);
  }
  return [...groups.values()].toSorted((a, b) => a.order - b.order);
}

export function canMoveTaskToWorkflowStage(
  task: CmmdBoardTask,
  destinationStageId: string,
  workflow: CmmdTaskWorkflow | null | undefined,
): boolean {
  const sourceStageId = task.workflowStageId ?? task.workflowResumeStageId;
  if (workflow == null || sourceStageId == null) return true;
  if (sourceStageId === destinationStageId) return true;
  return workflow.transitions.some(
    (transition) =>
      transition.fromStageId === sourceStageId && transition.toStageId === destinationStageId,
  );
}
